import axios, { AxiosError } from "axios";
import { getTenantSubdomain } from "@/config/brand";
import { getAgentAuthHeaders } from "./agentAuth";
import { isRateLimitError } from "./apiRateLimit";
import { posthog, isPostHogEnabled } from "./posthog";
import {
  announceWebAuthSessionChange,
  clearAuthSession,
  getAuthToken,
  getCsrfToken,
  hasWebAuthSession,
  markWebAuthSession,
  setCsrfToken,
} from "@/lib/auth";
import { storage } from "@/utils/storage";
import { joinUrlPath } from "@/lib/apiUrl";
import { getApiUrl } from "@/config/runtime";

// VITE_API_URL when set, otherwise same-origin `/api` (see config/runtime).
const BASE_URL = getApiUrl();

const MAX_RETRIES = 1;
const RETRY_DELAY = 1000;
const SAFE_METHODS = new Set(["get", "head", "options", "trace"]);
const CSRF_HEADER = "X-CSRFToken";

interface AxiosInstanceOptions {
  unwrapPaginated?: boolean;
}

declare module "axios" {
  export interface AxiosRequestConfig {
    __forceCsrf?: boolean;
    __skipAuthRedirect?: boolean;
    __authCookieRetry?: boolean;
    __csrfCookieRetry?: boolean;
    __skipAuthorization?: boolean;
    __retryCount?: number;
  }
}

export const buildLoginRedirectPath = ({
  pathname,
  search,
  hash,
}: Pick<Location, "pathname" | "search" | "hash">): string => {
  if (pathname === "/login") return "/login";
  const intendedPath = `${pathname}${search}${hash}`;
  return `/login?next=${encodeURIComponent(intendedPath)}`;
};

const joinBaseUrl = (path: string) => joinUrlPath(BASE_URL, path);

/** Clear the browser session and send the user back to the login screen. */
export const forceWebLogout = (): void => {
  clearAuthSession();
  storage.removeItem("user_info");
  window.location.href = buildLoginRedirectPath(window.location);
};

const readResponseHeader = (
  headers: unknown,
  name: string
): string | undefined => {
  if (!headers || typeof headers !== "object") return undefined;

  const maybeHeaders = headers as Record<string, unknown> & {
    get?: (headerName: string) => unknown;
  };
  if (typeof maybeHeaders.get === "function") {
    const value = maybeHeaders.get(name);
    return value == null ? undefined : String(value);
  }

  const value = maybeHeaders[name] ?? maybeHeaders[name.toLowerCase()];
  return value == null ? undefined : String(value);
};

const rememberAuthCookieResponse = (
  data: unknown,
  headers: unknown,
  requestUrl?: string
) => {
  const responseData = data as {
    auth_cookie?: unknown;
    csrf_token?: unknown;
  } | null;
  const csrfToken =
    responseData?.csrf_token ?? readResponseHeader(headers, CSRF_HEADER);

  if (responseData?.auth_cookie === true) {
    markWebAuthSession(csrfToken);
    if (requestUrl?.split("?", 1)[0].endsWith("/token-auth/")) {
      announceWebAuthSessionChange("signed-in");
    }
    return;
  }

  setCsrfToken(csrfToken);
};

let csrfRequest: Promise<string | null> | null = null;

const fetchCsrfToken = async (): Promise<string | null> => {
  if (!csrfRequest) {
    csrfRequest = axios
      .get(joinBaseUrl("/auth/csrf/"), {
        timeout: 5_000,
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          ...(getTenantSubdomain()
            ? { "X-Tenant": getTenantSubdomain() as string }
            : {}),
        },
      })
      .then((response) => {
        const token =
          (response.data as { csrf_token?: unknown } | null)?.csrf_token ??
          readResponseHeader(response.headers, CSRF_HEADER);
        setCsrfToken(token);
        return typeof token === "string" && token.trim() ? token : null;
      })
      .finally(() => {
        csrfRequest = null;
      });
  }

  return csrfRequest;
};

const ensureCsrfToken = async (): Promise<string | null> =>
  getCsrfToken() ?? fetchCsrfToken();

const isCsrfFailure = (error: AxiosError): boolean => {
  if (error.response?.status !== 403) return false;
  const data = error.response.data;
  const detail =
    typeof data === "string"
      ? data
      : data && typeof data === "object" && "detail" in data
        ? String((data as { detail?: unknown }).detail ?? "")
        : "";
  return /csrf/i.test(detail);
};

export const createAxiosInstance = (
  requiresAuth: boolean = true,
  options: AxiosInstanceOptions = {}
) => {
  const { unwrapPaginated = true } = options;

  const instance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor — resolve X-Tenant + auth token fresh per request.
  // X-Tenant must be recomputed each call because the user could navigate
  // between subdomains (or the SPA could be loaded on a new subdomain)
  // without the axios instance being re-created.
  instance.interceptors.request.use(
    async (config) => {
      const tenantSubdomain = getTenantSubdomain();
      if (tenantSubdomain) {
        config.headers["X-Tenant"] = tenantSubdomain;
      } else {
        delete config.headers["X-Tenant"];
      }
      const method = (config.method || "get").toLowerCase();
      const shouldAttachCsrf =
        !SAFE_METHODS.has(method) &&
        (hasWebAuthSession() || config.__forceCsrf === true);
      if (requiresAuth) {
        Object.assign(config.headers, getAgentAuthHeaders());
        const token = getAuthToken();
        // Browser sessions authenticate HTTP through the shared HttpOnly cookie.
        // Keeping the per-tab token off HTTP requests prevents another tab's
        // token rotation from turning a valid cookie session into a 401.
        if (token && !hasWebAuthSession() && !config.__skipAuthorization) {
          config.headers.Authorization = `Token ${token}`;
        } else {
          delete config.headers.Authorization;
        }
      }
      if (shouldAttachCsrf) {
        const csrfToken = await ensureCsrfToken();
        if (csrfToken) {
          config.headers[CSRF_HEADER] = csrfToken;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor — unwrap paginated responses
  instance.interceptors.response.use(
    (response) => {
      rememberAuthCookieResponse(
        response.data,
        response.headers,
        response.config.url
      );
      // Auto-unwrap DRF pagination: { count, next, results: [...] } → [...]
      const data = response.data;
      if (
        unwrapPaginated &&
        data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        Array.isArray(data.results) &&
        "count" in data
      ) {
        response.data = data.results;
      }
      return response;
    },
    async (error: AxiosError) => {
      const config = error.config;
      if (!config) return Promise.reject(error);

      config.__retryCount = config.__retryCount || 0;

      const isRetryable =
        !isRateLimitError(error) &&
        (!error.response ||
          error.response.status === 503 ||
          error.code === "ECONNABORTED" ||
          error.code === "ERR_NETWORK");

      if (
        isRetryable &&
        config.__retryCount < MAX_RETRIES &&
        config.method === "get"
      ) {
        config.__retryCount += 1;
        const delay = RETRY_DELAY * Math.pow(2, config.__retryCount - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return instance(config);
      }

      // A web login rotates the single backend token and shared HttpOnly cookie.
      // Other tabs can still hold the old token in sessionStorage, and DRF checks
      // that stale Authorization header before cookie authentication. Retry once
      // without the stale header so the current shared cookie can authenticate.
      const canRetryWithCookie =
        requiresAuth &&
        error.response?.status === 401 &&
        !config.__skipAuthRedirect &&
        !config.__authCookieRetry &&
        hasWebAuthSession() &&
        Boolean(readResponseHeader(config.headers, "Authorization"));

      if (canRetryWithCookie) {
        config.__authCookieRetry = true;
        config.__skipAuthorization = true;
        return instance(config);
      }

      // A deployment can change the API cookie scope while a browser still
      // holds a JS-cached CSRF token from the previous policy. Django then sees
      // the header but no matching cookie. Refresh the cookie/token pair and
      // retry the unsafe request once so existing tabs recover automatically.
      if (!config.__csrfCookieRetry && isCsrfFailure(error)) {
        config.__csrfCookieRetry = true;
        const csrfToken = await fetchCsrfToken();
        if (csrfToken) {
          config.headers[CSRF_HEADER] = csrfToken;
          return instance(config);
        }
      }

      // Public auth requests (login, 2FA, branding) must surface their own 401
      // errors instead of destroying an otherwise valid browser session.
      if (
        requiresAuth &&
        error.response?.status === 401 &&
        !config.__skipAuthRedirect
      ) {
        clearAuthSession();
        storage.removeItem("user_info");
        window.location.href = buildLoginRedirectPath(window.location);
        return Promise.reject(
          new Error("Session expired. Please sign in again.")
        );
      }

      // The membership may have been switched to mobile-only while this browser
      // session was open. Kick the tab out as soon as the API reports it.
      const errorCode = (error.response?.data as { code?: string } | undefined)
        ?.code;
      if (error.response?.status === 403 && errorCode === "mobile_only") {
        if (
          typeof window !== "undefined" &&
          window.location.pathname !== "/login"
        ) {
          forceWebLogout();
        }
        return Promise.reject(error);
      }

      // Forward server-side failures to PostHog so we can see failure
      // rates per endpoint without trawling logs. 4xx-but-not-401 are
      // user errors and skipped to keep volume sane.
      if (isPostHogEnabled()) {
        const status = error.response?.status ?? 0;
        const isServerError = status >= 500 || status === 0;
        if (isServerError) {
          posthog.capture("api_request_failed", {
            method: (config.method || "get").toUpperCase(),
            url: config.url,
            status,
            code: error.code,
          });
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Create instances for different use cases
export const authApi = createAxiosInstance(false);
export const api = createAxiosInstance(true);
export const schemaApi = createAxiosInstance(true, { unwrapPaginated: false });
